import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { format } from 'date-fns'

import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import EditIcon from '@mui/icons-material/Edit'

import saveSuccess from '../../icons/saveSuccess.svg'

import axios from '../../dataClient/dataClient'
import { separateFileStatuses } from '../../util/categoryConsts'
import { getFileSize } from '../../util/strings'
import { theme } from '../../themes/theme'
import { FlexRow } from '../styles/Containers'
import { Container, Stackable, FirstItem, LargeItem, MedItem, SmallItem, SmallItemRight } from '../styles/CategoryStyles'
import { MedBodyText, BoldMedBodyText, BoldSmallBodyText } from '../styles/Text'
import { DRAG_HANDLE, SUCCESS, SUCCESS_LIGHT } from '../styles/colors'
import { TableCheckbox, TableCheckboxCell } from '../styles/Checkbox'
import { SecondaryIconButton } from '../common/Button'
import FileExtension from '../common/FileExtension'
import ArchiveFileButton from '../files/ArchiveFileButton'
import InlineRenameEditor from './InlineRenameEditor'
import RestoreFileToCategoryButton from './RestoreFileToCategoryButton'

export const RENAMED_PILL_MS = 3000

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  width: 22px;
  min-width: 22px;
  color: ${DRAG_HANDLE};
  cursor: grab;
  transition: color 0.12s ease;

  :hover,
  :focus-visible {
    color: ${theme.colors.primary};
  }

  :focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
    border-radius: 2px;
  }

  :active {
    cursor: grabbing;
  }

  svg {
    font-size: 22px;
  }
`

const Row = styled(FlexRow)`
  padding: 5px 20px;
  align-items: center;

  cursor: pointer;
  background: ${props => (props.active || props.isSelected ? theme.colors.primaryExtraLight : 'transparent')};
  :hover {
    background: ${props => (props.suppressHover && !props.active && !props.isSelected ? 'transparent' : theme.colors.primaryExtraLight)};
  }

  :hover .rename-file-button:not([disabled]),
  .rename-file-button.renaming {
    color: ${theme.colors.primary};
    border-color: ${theme.colors.primary};
    background: ${theme.colors.primaryExtraLight};
  }
`

const DateTimeContainer = styled(FlexRow)`
  gap: 5px;
  flex-wrap: wrap;

  /* Wrap between date and time, never inside either. */
  > * {
    white-space: nowrap;
  }
`

const Actions = styled(FlexRow)`
  gap: 5px;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: nowrap;
  flex-shrink: 0;
`

const FilenameCell = styled(FlexRow)`
  gap: 10px;
  align-items: center;
  flex-wrap: nowrap;
  min-width: 0;
`

const RenamedPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 2px 8px 2px 8px;
  border: 1px solid ${SUCCESS};
  border-radius: ${theme.borderRadius.button};
  background: ${SUCCESS_LIGHT};
  white-space: nowrap;

  .MuiSvgIcon-root {
    width: 14px;
    height: 14px;
    color: ${SUCCESS};
  }
`

const SaveIcon = styled.img`
  width: 13px;
`

function CategoryOriginalFile({
  file,
  foiId,
  categories = [],
  isRemoved = false,
  reorderable = false,
  renameable = false,
  selectable = false,
  selected = false,
  onToggleSelect = () => {},
  active = false,
  hovered = false,
  suppressHover = false,
  disabled = false,
  onRowClick = () => {},
  onMove = () => {},
  canMoveUp = false,
  canMoveDown = false,
  handleRemoveFile = () => {},
  handleRestoreFile = () => {},
  handleRenameFile = () => {},
  renamedPillMs = RENAMED_PILL_MS
}) {
  const [isDownloading, setIsDownloading] = useState(false)

  const [isRenaming, setIsRenaming] = useState(false)
  const [isSavingRename, setIsSavingRename] = useState(false)
  const [showRenamed, setShowRenamed] = useState(false)
  const renamedTimer = useRef(null)

  useEffect(() => {
    return () => {
      if (renamedTimer.current) clearTimeout(renamedTimer.current)
    }
  }, [])

  // Keep reorder keystrokes from triggering the file row.
  function handleHandleKeyDown(e) {
    if (e.key === 'ArrowUp' && canMoveUp) {
      e.preventDefault()
      e.stopPropagation()
      onMove(-1)
    } else if (e.key === 'ArrowDown' && canMoveDown) {
      e.preventDefault()
      e.stopPropagation()
      onMove(1)
    }
  }

  function handleFileDownload() {
    if (isDownloading || isRenaming) return

    setIsDownloading(true)
    axios
      .get(`/file/${file.id}/download`, { responseType: 'blob' })
      .then(response => {
        onRowClick(response, file)
      })
      .catch(e => {
        console.error('An error occurred while fetching the document', file.filename, e)
      })
      .finally(() => {
        setIsDownloading(false)
      })
  }

  function startRename() {
    setShowRenamed(false)
    setIsRenaming(true)
  }

  function cancelRename() {
    if (isSavingRename) return
    setIsRenaming(false)
  }

  function handleRenamed(updated) {
    setIsRenaming(false)
    setShowRenamed(true)
    if (renamedTimer.current) clearTimeout(renamedTimer.current)
    renamedTimer.current = setTimeout(() => setShowRenamed(false), renamedPillMs)
    handleRenameFile(updated)
  }

  const fileExtension = file.status === 'RAW' ? file.fileExtension : file.originalFileExtension
  const displayDate = isRemoved && file.archivedAt ? file.archivedAt : file.createdAt
  const stopPropagation = e => e.stopPropagation()

  // Keep clicks and drags on the checkbox cell from reaching the row.
  function handleCheckboxDragStart(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <Row active={active || hovered} isSelected={selected} suppressHover={suppressHover} onClick={handleFileDownload} gap="30px">
      {selectable && (
        <TableCheckboxCell draggable={false} onClick={stopPropagation} onDragStart={handleCheckboxDragStart}>
          <TableCheckbox checked={selected} onChange={onToggleSelect} inputProps={{ 'aria-label': `Select ${file.filename}` }} />
        </TableCheckboxCell>
      )}
      {reorderable && (
        <DragHandle
          role="button"
          tabIndex={0}
          aria-label={`Reorder ${file.filename}. Use the up and down arrow keys to move this file.`}
          onClick={stopPropagation}
          onKeyDown={handleHandleKeyDown}>
          <DragIndicatorIcon />
        </DragHandle>
      )}
      <FirstItem>{fileExtension !== undefined && <FileExtension fileExtension={fileExtension} />}</FirstItem>
      <Container>
        <Stackable width="100%">
          <LargeItem isRemoved={isRemoved}>
            {isRenaming ? (
              <InlineRenameEditor file={file} onSaved={handleRenamed} onCancel={cancelRename} onSavingChange={setIsSavingRename} />
            ) : (
              <FilenameCell>
                <BoldMedBodyText>{file.filename}</BoldMedBodyText>
                {showRenamed && (
                  <RenamedPill role="status" data-testid="renamed-pill">
                    <SaveIcon src={saveSuccess} />
                    <BoldSmallBodyText>Renamed</BoldSmallBodyText>
                  </RenamedPill>
                )}
              </FilenameCell>
            )}
          </LargeItem>
          {isRemoved && (
            <MedItem>
              <BoldMedBodyText>{file.archivedReason}</BoldMedBodyText>
            </MedItem>
          )}
          <SmallItem isRemoved={isRemoved}>
            <DateTimeContainer>
              <BoldMedBodyText>{format(new Date(displayDate), 'MMM-dd-yyyy')}</BoldMedBodyText>
              <MedBodyText>{format(new Date(displayDate), 'hh:mm a')}</MedBodyText>
            </DateTimeContainer>
          </SmallItem>
          <SmallItemRight isRemoved={isRemoved}>
            <BoldMedBodyText>{getFileSize(file.size)}</BoldMedBodyText>
          </SmallItemRight>
        </Stackable>
      </Container>
      <Actions onClick={stopPropagation}>
        {renameable && !isRemoved && (
          <SecondaryIconButton
            className={`rename-file-button${isRenaming ? ' renaming' : ''}`}
            aria-label="Rename File"
            aria-pressed={isRenaming}
            onClick={isRenaming ? cancelRename : startRename}
            disabled={disabled || isSavingRename}>
            <EditIcon />
          </SecondaryIconButton>
        )}
        {isRemoved ? (
          <RestoreFileToCategoryButton
            fileId={file.id}
            name={file.filename}
            foiId={foiId}
            categories={categories}
            onSuccess={handleRestoreFile}
            disabled={disabled}
          />
        ) : (
          <ArchiveFileButton fileId={file.id} name={file.filename} onSuccess={handleRemoveFile} disabled={disabled || isRenaming} />
        )}
      </Actions>
    </Row>
  )
}

CategoryOriginalFile.propTypes = {
  file: PropTypes.shape({
    id: PropTypes.string.isRequired,
    filename: PropTypes.string.isRequired,
    status: PropTypes.oneOf(separateFileStatuses),
    fileExtension: PropTypes.string.isRequired,
    originalFileExtension: PropTypes.string,
    size: PropTypes.number.isRequired,
    createdAt: PropTypes.instanceOf(Date).isRequired,
    archivedAt: PropTypes.instanceOf(Date),
    archivedReason: PropTypes.string
  }).isRequired,
  foiId: PropTypes.string.isRequired,
  categories: PropTypes.array,
  isRemoved: PropTypes.bool,
  reorderable: PropTypes.bool,
  renameable: PropTypes.bool,
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  onToggleSelect: PropTypes.func,
  active: PropTypes.bool,
  hovered: PropTypes.bool,
  suppressHover: PropTypes.bool,
  disabled: PropTypes.bool,
  onRowClick: PropTypes.func,
  onMove: PropTypes.func,
  canMoveUp: PropTypes.bool,
  canMoveDown: PropTypes.bool,
  handleRemoveFile: PropTypes.func,
  handleRestoreFile: PropTypes.func,
  handleRenameFile: PropTypes.func,
  renamedPillMs: PropTypes.number
}

export default CategoryOriginalFile
